/**
 * Groups ticketing items the way the public ticketing site does.
 *
 * Order: active tabs by `tab.sort_order`, then `sort_order` on the item, then
 * name. Items with no tab (or an inactive tab, which the site's anonymous
 * role cannot see) go on a trailing default tab.
 *
 * Keep this in step with `ticketing/src/utils/tabLayout.ts`.
 */
import { TicketingItem } from "../../../types/tickets";

export const DEFAULT_TAB_NAME = "Tickets";

export interface TabGroup<T> {
  key: string;
  name: string;
  sortOrder: number;
  items: T[];
}

type Groupable = Pick<TicketingItem, "id" | "name" | "sort_order" | "tab">;

export function groupItemsByTab<T extends Groupable>(items: T[]): TabGroup<T>[] {
  const groups = new Map<string, TabGroup<T>>();

  for (const item of items) {
    const tab = item.tab && item.tab.is_active !== false ? item.tab : null;
    const key = tab ? `tab-${tab.id}` : "default";
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        name: tab ? tab.name : DEFAULT_TAB_NAME,
        sortOrder: tab ? tab.sort_order ?? 0 : Number.MAX_SAFE_INTEGER,
        items: [],
      };
      groups.set(key, group);
    }
    group.items.push(item);
  }

  const byItemOrder = (a: T, b: T) =>
    (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
    String(a.name ?? "").localeCompare(String(b.name ?? ""));

  return Array.from(groups.values())
    .map((g) => ({ ...g, items: [...g.items].sort(byItemOrder) }))
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
}
